import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {KB_TAG} from 'app/kbs/api';
import {sweetAPI} from 'app/store/sweet-api';
import {showLoadingBar, showMessages} from 'app/store/sweet-api/utils';
import {ACM_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {CreateAcmProps} from './types';

import type {
  AcmCertificateSerializer,
  UserJobSerializedType,
} from 'sweet-types';

export const ACM_TAG = 'acm';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [ACM_TAG, KB_TAG]})
  .injectEndpoints({
    endpoints: (build) => ({
      acm: build.query<AcmCertificateSerializer, {url: string}>({
        query: ({url}) => ({
          url,
        }),
        providesTags: (response) =>
          response != null
            ? [
                {type: ACM_TAG, id: `${ACM_API_URL}${response.hashid}/`},
                {type: ACM_TAG, id: 'list'},
              ]
            : [],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
        },
      }),
      createAcm: build.mutation<unknown, CreateAcmProps>({
        queryFn: (
          body,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: ACM_API_URL,
                    method: 'POST',
                    body,
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: (response: UserJobSerializedType) => {
          return [
            {
              type: ACM_TAG,
              id: 'list',
            },
            {
              type: KB_TAG,
              id: 'list',
            },
            {
              type: ACM_TAG,
              id: 'url' in response.result ? response.result.url : '',
            },
          ];
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onError: {
              fallback: () => gt('Unable to create validation record.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
      }),
      deleteAcm: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => {
          return [
            {
              type: ACM_TAG,
              id: 'list',
            },
            {
              type: KB_TAG,
              id: 'list',
            },
          ];
        },
      }),
      startValidation: build.mutation<unknown, {url: string}>({
        queryFn: (
          {url},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${url}validate/`,
                    method: 'PATCH',
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: (response: UserJobSerializedType) => {
          return [
            {
              type: ACM_TAG,
              id: 'list',
            },
            {
              type: ACM_TAG,
              id: 'url' in response.result ? response.result.url : '',
            },
          ];
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Unable to start validation.'),
          })(args, api);
        },
      }),
      confirmConfiguration: build.mutation<unknown, {url: string}>({
        queryFn: (
          {url},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${url}confirmation/`,
                    method: 'PATCH',
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => {
          return [
            {
              type: ACM_TAG,
              id: 'list',
            },
          ];
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () =>
              gt('There was a problem with the DNS for your knowledge base.'),
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useAcmQuery: useAcm,
  useCreateAcmMutation: useCreateAcm,
  useDeleteAcmMutation: useDeleteAcm,
  useStartValidationMutation: useStartValidation,
  useConfirmConfigurationMutation: useConfirmConfiguration,
} = extendedAPI;
